import React from 'react';

import {
    IconButton, Slide,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon              from '@material-ui/icons/Close';
import { CircularProgress, Box } from '@material-ui/core';

import './CarrierInvoiceDetailView.css';
import { ROWS_PER_PAGE_LABEL } from './constants';
import CustomTableHeader from '../../global/Table/CustomTableHeader';
import SearchableTable from '../../global/Search/SearchableTable';
import FilterIcon from '../../shipments/FilterIcon';
import CarrierInvoiceDetailFiltersDialog from './CarrierInvoiceDetailFiltersDialog';
import FilterTag from '../../shipments/FilterTag';

const parseDecimal = (v) => {
    if (v == null) return null;
    if (typeof v === 'object' && v.$numberDecimal != null) return parseFloat(v.$numberDecimal);
    const f = parseFloat(v);
    return isNaN(f) ? null : f;
};

const getNestedValue = (obj, dotPath) =>
    dotPath.split('.').reduce((acc, k) => (acc != null ? acc[k] : null), obj);

const formatDollar = (n) => {
    if (n === null || n === undefined) return '—';
    if (n === 0) return '—';
    const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (n < 0 ? '-$' : '$') + abs;
};

const shortAddrFromRecipient = (r) =>
    r ? [r.city, r.province, r.postalCode].filter(Boolean).join(', ').toUpperCase() : '';

const fullAddrFromRecipient = (r) =>
    r ? [r.streetAddress1, r.streetAddress2, r.city, r.province, r.postalCode].filter(Boolean).join(', ').toUpperCase() : '';

const addrFromRecipient = shortAddrFromRecipient;

const resolveChargeNum = (value) => {
    if (value !== null && value !== undefined && typeof value === 'object' && value.$numberDecimal == null) {
        return Object.values(value).reduce((sum, v) => sum + (parseDecimal(v) || 0), 0);
    }
    return parseDecimal(value);
};

const ChargeBadge = ({ value }) => {
    const n = resolveChargeNum(value);
    if (n === null || n === 0) return <span className="charge-cell--zero">—</span>;
    return <span className={n < 0 ? 'charge-cell--negative' : 'charge-cell--positive'}>{formatDollar(n)}</span>;
};

const AddressBadge = ({ match }) => {
  if (match === null || match === undefined)
    return <span className="detail-badge detail-badge--na">N/A</span>;
  return match ? (
    <span className="detail-badge detail-badge--ok">
      <CheckCircleOutlineIcon style={{ fontSize: '0.875rem' }} /> OK
    </span>
  ) : (
    <span className="detail-badge detail-badge--addr-diff">
      <CloseIcon style={{ fontSize: '0.875rem' }} /> Diff
    </span>
  );
};

const MatchBadge = ({ mismatch, hasRate }) => {
  if (!hasRate)
    return <span className="detail-badge detail-badge--na">N/A</span>;
  return mismatch ? (
    <span className="detail-badge detail-badge--addr-diff">
      <CloseIcon style={{ fontSize: '0.875rem' }} /> Diff
    </span>
  ) : (
    <span className="detail-badge detail-badge--ok">
      <CheckCircleOutlineIcon style={{ fontSize: '0.875rem' }} /> OK
    </span>
  );
};


const ChargeColHeader = ({ label }) => {
    const words = label.trim().split(/\s+/);
    if (words.length <= 1) return <>{label}</>;
    const top    = words.slice(0, -1).join(' ');
    const bottom = words[words.length - 1];
    return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
            <span>{top}</span>
            <span>{bottom}</span>
        </span>
    );
};

const getSortValue = (row, key) => {
    switch (key) {
        case 'trackingId':        return row.trackingId || '';
        case 'customerReference': return row.details?.customerReference || '';
        case 'shipDate':          return row.details?.shipDate || '';
        case 'delivered':         return row.details?.delivered || '';
        case 'serviceType':       return row.details?.serviceType || '';
        case 'zone':              return row.details?.zone || '';
        case 'packages':          return parseFloat(row.details?.packages) || 0;
        case 'actualWeight':      return parseFloat(row.details?.actualWeight) || 0;
        case 'ratedWeight':       return parseFloat(row.details?.ratedWeight) || 0;
        case 'recipientAddress':  return addrFromRecipient(row.recipient);
        case 'invoiceTotal':      return parseDecimal(row.charges?.total ?? row.charges?.subtotal) || 0;
        case 'quotedRate':        return row.shipmentRate ? parseFloat(row.shipmentRate) : 0;
        case 'match':             return row._mismatch ? 1 : 0;
        default: {
            // charge column (dot-path key)
            const v = getNestedValue(row.charges, key);
            return resolveChargeNum(v) || 0;
        }
    }
};

const applySort = (rows, orderBy, order) => {
    if (!orderBy) return rows;
    return [...rows].sort((a, b) => {
        const va = getSortValue(a, orderBy);
        const vb = getSortValue(b, orderBy);
        if (va < vb) return order === 'asc' ? -1 : 1;
        if (va > vb) return order === 'asc' ? 1 : -1;
        return 0;
    });
};

const rowMatchesSearch = (row, search, visibleChargeColumns) => {
    if (!search) return true;
    const q = search.toLowerCase();

    const fields = [
        row.trackingId,
        row.details?.customerReference,
        row.details?.shipDate,
        row.details?.delivered,
        row.details?.serviceType,
        row.details?.zone,
        String(row.details?.packages ?? ''),
        String(row.details?.actualWeight ?? ''),
        String(row.details?.ratedWeight ?? ''),
        addrFromRecipient(row.recipient),
    ];

    if (fields.some(f => f && String(f).toLowerCase().includes(q))) return true;

    // search charge values
    for (const col of visibleChargeColumns) {
        const v = getNestedValue(row.charges, col.key);
        const n = resolveChargeNum(v);
        if (n != null && formatDollar(n).toLowerCase().includes(q)) return true;
    }

    const invoiceTotal = parseDecimal(row.charges?.total ?? row.charges?.subtotal);
    if (invoiceTotal != null && formatDollar(invoiceTotal).toLowerCase().includes(q)) return true;

    const quotedRate = row.shipmentRate ? formatDollar(parseFloat(row.shipmentRate)) : '';
    if (quotedRate.toLowerCase().includes(q)) return true;

    return false;
};

const noShrink = {
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
};

class CarrierInvoiceDetailView extends SearchableTable {

    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            order: 'asc',
            orderBy: null,
            searchValue: '',
            isSearch: false,
             filtersMap: {},
            showFilters: false,
            filtersMapBeforeOpenDialog: {},
            deletedTagsWithCookies: [],
        };
    }

    componentDidMount() {
        const { carrierInvoiceRow, invoice } = this.props;
        if (carrierInvoiceRow) return;
        if (invoice?.carrierInvoiceId) {
            this.props.getCarrierInvoiceDetailRequest({ carrierInvoiceId: invoice.carrierInvoiceId });
        }
    }

    componentDidUpdate(prevProps) {
        const { carrierInvoiceRow, invoice, fetchDetail, clearDetail } = this.props;
        if (carrierInvoiceRow) return;
        if (prevProps.invoice?.carrierInvoiceId !== invoice?.carrierInvoiceId) {
            clearDetail();
            if (invoice?.carrierInvoiceId) fetchDetail(invoice.carrierInvoiceId);
        }
    }

    componentWillUnmount() {
        this.props.clearCarrierInvoiceDetail();
    }

    // ── charge column helpers ──────────────────────────────────────────────

    getVisibleChargeColumns(rows) {
        const { chargeColumns } = this.props;
        if (chargeColumns && chargeColumns.length > 0) return chargeColumns;
        if (!rows || rows.length === 0) return [];

        const keySet = new Set();
        rows.forEach(row => {
            const flatten = (obj, prefix = '') => {
                if (!obj || typeof obj !== 'object') return;
                Object.entries(obj).forEach(([k, v]) => {
                    if (k.toLowerCase().endsWith('total')) return;
                    const key = prefix ? `${prefix}.${k}` : k;
                    if (v && typeof v === 'object' && v.$numberDecimal == null) flatten(v, key);
                    else keySet.add(key);
                });
            };
            flatten(row.charges);
        });

        return [...keySet].sort().map(key => ({
            key,
            label: key.split('.').pop()
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, s => s.toUpperCase())
                .trim(),
        }));
    }

    reorderChargeColumns = (columns) => {
        if (!columns || columns.length === 0) return [];
        const taxColumns   = [];
        const otherColumns = [];
        columns.forEach(col => {
            const k = col.key.toLowerCase();
            const l = (col.label || '').toLowerCase();
            if (k.includes('gst') || k.includes('hst') || l.includes('gst') || l.includes('hst'))
                taxColumns.push(col);
            else
                otherColumns.push(col);
        });
        return [...otherColumns, ...taxColumns];
    };

    getTotals(rows, visibleChargeColumns) {
        if (!rows || rows.length === 0) return null;
        return rows.reduce((acc, r) => {
            visibleChargeColumns.forEach(col => {
                const raw = getNestedValue(r.charges, col.key);
                let v = 0;
                if (raw != null) {
                    v = typeof raw === 'object' && raw.$numberDecimal == null
                        ? Object.values(raw).reduce((s, sv) => s + (parseDecimal(sv) || 0), 0)
                        : parseDecimal(raw) || 0;
                }
                acc[col.key] = (acc[col.key] || 0) + v;
            });
            acc._invoiceTotal += parseDecimal(r.charges?.total ?? r.charges?.subtotal) || 0;
            acc._quotedTotal  += r.shipmentRate ? parseFloat(r.shipmentRate) : 0;
            return acc;
        }, { _invoiceTotal: 0, _quotedTotal: 0 });
    }

    // ── event handlers ─────────────────────────────────────────────────────

    onSearchValueChange = (e) => {
        this.setState({
            isSearch: e.target.value.length > 0,
            searchValue: e.target.value,
        });
        this.props.setPage(0);
    };

    handleRequestSort = (property) => {
        const isAsc = this.state.orderBy === property && this.state.order === 'asc';
        this.setState({ order: isAsc ? 'desc' : 'asc', orderBy: property });
        this.props.setPage(0);
    };

    handleChangePage = (event, newPage) => this.props.setPage(newPage);

    handleChangeRowsPerPage = (event) => {
        this.props.setRowsPerPage(event.target.value);
        this.props.setPage(0);
    };

    // ── sort label helper ──────────────────────────────────────────────────

    sortCell(key, children, align = 'left') {
        const { order, orderBy } = this.state;
        return (
            <TableCell align={align} style={{ ...noShrink, cursor: 'pointer' }}>
                <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : 'asc'}
                    onClick={() => this.handleRequestSort(key)}
                >
                    {children}
                </TableSortLabel>
            </TableCell>
        );
    }

    toggleFiltersDialog = () => {
        this.setState({
            showFilters: !this.state.showFilters,
            filtersMapBeforeOpenDialog: { ...this.state.filtersMap }
        })
    }


     onFiltersInputChange = (name, val) => {
        const filtersMap = { ...this.state.filtersMap };
        if (!val || val.length === 0) {
            delete filtersMap[name];
        } else {
            filtersMap[name] = val;
        }
        this.setState({ filtersMap });
    }

    onFiltersClose = (applyFilters) => {
        this.setState({
            showFilters: false,
            filtersMap: applyFilters
                ? { ...this.state.filtersMap }
                : { ...this.state.filtersMapBeforeOpenDialog },
        });
    }

    getName = (key) => {
        switch (key) {
            case 'addressMatch':   return 'Address Match';
            case 'quoteRateMatch': return 'Quote Rate Match';
            default:               return key;
        }
    }

    createTags = (filtersMap, onClick) => {
        if (!filtersMap) return;

        return Object.entries(filtersMap).flatMap(([key, val]) => {
            const values = Array.isArray(val) ? val : [val];
            return values.map(v => (
                <div className="shipment-tag-invoice" key={`${key}-${v}`}>
                    <FilterTag
                        tag={key}
                        id={`${key}-${v}`}
                        removeTag={(e) => onClick(e, key, v)}
                        name={`${this.getName(key)}: ${v}`}
                    />
                </div>
            ));
        });
    }

    clearFiltersMap = () => {
        this.setState({ filtersMap: {} });
    }

    onTagClicked = (e, key, value) => {
        const filtersMap = { ...this.state.filtersMap };
        if (Array.isArray(filtersMap[key])) {
            const updated = filtersMap[key].filter(v => v !== value);
            if (updated.length === 0) {
                delete filtersMap[key];
            } else {
                filtersMap[key] = updated;
            }
        } else {
            delete filtersMap[key];
        }
        this.setState({ filtersMap });
    }

    onTagClickedWhenDialogueOpen = (e, key, value) => {
        this.onTagClicked(e, key, value);
    }

    // ── render ─────────────────────────────────────────────────────────────

    render() {
        const { invoice, isLoading, carrierInvoiceRow } = this.props;
        const { order, orderBy, searchValue } = this.state;

        const allRows = carrierInvoiceRow ?? [];

        const visibleChargeColumns = this.reorderChargeColumns(this.getVisibleChargeColumns(allRows));
        const totals               = this.getTotals(allRows, visibleChargeColumns);
        const variance             = totals ? totals._invoiceTotal - totals._quotedTotal : 0;
        const mismatchCount        = allRows.filter(r => r._mismatch).length;

        // ── search → sort → paginate ──

        const applyFilters = (rows, filtersMap) => {
            if (!filtersMap || Object.keys(filtersMap).length === 0) return rows;

            return rows.filter(row => {
                if (filtersMap.addressMatch?.length > 0) {
                    const label = row._addressMatch === true ? 'OK'
                                : row._addressMatch === false ? 'Diff'
                                : 'N/A';
                    if (!filtersMap.addressMatch.includes(label)) return false;
                }

                if (filtersMap.quoteRateMatch?.length > 0) {
                    const label = row.shipmentRate == null ? 'N/A'
                                : row._mismatch ? 'Diff'
                                : 'OK';
                    if (!filtersMap.quoteRateMatch.includes(label)) return false;
                }

                return true;
            });
        };

        const filteredRows  = applyFilters(
            allRows.filter(r => rowMatchesSearch(r, searchValue, visibleChargeColumns)),
            this.state.filtersMap
        );

        // const filteredRows = allRows.filter(r => rowMatchesSearch(r, searchValue, visibleChargeColumns));
        const sortedRows   = applySort(filteredRows, orderBy, order);
        const pagedRows    = sortedRows.slice(
            this.props.page * this.props.rowsPerPage,
            (this.props.page + 1) * this.props.rowsPerPage,
        );

        const showTrackingId   = allRows.some(r => r.trackingId);
        const showCustomerRef  = allRows.some(r => r.details?.customerReference);
        const showShipDate     = allRows.some(r => r.details?.shipDate);
        const showDelivered    = allRows.some(r => r.details?.delivered);
        const showServiceType  = allRows.some(r => r.details?.serviceType);
        const showZone         = allRows.some(r => r.details?.zone);
        const showPackages     = allRows.some(r => r.details?.packages);
        const showActualWeight = allRows.some(r => r.details?.actualWeight);
        const showRatedWeight  = allRows.some(r => r.details?.ratedWeight);

        const baseColCount = [
            showTrackingId, showCustomerRef, showShipDate, showDelivered,
            showServiceType, showZone, showPackages, showActualWeight, showRatedWeight,
            true, true,
        ].filter(Boolean).length;



        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!allRows || allRows.length === 0) {
            return (
                <Box textAlign="center" p={4} style={{ color: '#888' }}>
                    No carrier invoice items found
                </Box>
            );
        }

        return (
          <div>
              {/* ── Summary bar ── */}
              <div className="detail-summary-bar">
                  <div className="detail-summary-item">
                      <span className="detail-summary-label">Invoice</span>
                      <span className="detail-summary-value">
                  {invoice?.invoiceNumber}
                </span>
                  </div>
                  <div className="detail-summary-item">
                      <span className="detail-summary-label">Carrier</span>
                      <span className="detail-summary-value">
                  {invoice?.carrierName}
                </span>
                  </div>
                  <div className="detail-summary-item">
                      <span className="detail-summary-label">Date</span>
                      <span className="detail-summary-value">
                  {invoice?.invoiceDate}
                </span>
                  </div>

                  {totals && (
                    <>
                        <div className="detail-summary-item">
                            <span className="detail-summary-label">Shipments</span>
                            <span className="detail-summary-value">
                      {allRows.length}
                    </span>
                        </div>
                        <div className="detail-summary-item">
                            <span className="detail-summary-label">Invoice Total</span>
                            <span className="detail-summary-value">
                      ${totals._invoiceTotal.toFixed(2)}
                    </span>
                        </div>
                        <div className="detail-summary-item">
                            <span className="detail-summary-label">Quoted Total</span>
                            <span className="detail-summary-value">
                      ${totals._quotedTotal.toFixed(2)}
                    </span>
                        </div>
                        <div className="detail-summary-item">
                            <span className="detail-summary-label">Variance</span>
                            {/* ✅ red when |variance| >= 0.01 */}
                            <span
                              className={`detail-summary-value ${
                                Math.abs(variance) >= 0.01
                                  ? 'detail-summary-value--warning'
                                  : 'detail-summary-value--ok'
                              }`}
                            >
                      {variance >= 0 ? '+' : ''}${variance.toFixed(2)}
                    </span>
                        </div>
                        {mismatchCount > 0 && (
                          <div className="detail-summary-item">
                              <span className="detail-summary-label">Mismatches</span>
                              <span className="detail-summary-value detail-summary-value--mismatch">
                        {mismatchCount}
                      </span>
                          </div>
                        )}
                    </>
                  )}
              </div>

              {/* ── Table ── */}

              <div className="detail-table-wrapper">

                  <div className="filter-tag-wrapper-invoice">
                      <div>
                          {this.createTags(
                            this.state.filtersMap,
                            this.state.showFilters
                              ? this.onTagClickedWhenDialogueOpen
                              : this.onTagClicked
                          )}
                      </div>
                  </div>

                  <CustomTableHeader
                    searchText={searchValue}
                    onSearchValueChange={this.onSearchValueChange}
                    pagination={
                        <TablePagination
                          className="table-pagination"
                          labelRowsPerPage={ROWS_PER_PAGE_LABEL}
                          rowsPerPageOptions={[
                              5,
                              25,
                              50,
                              100,
                              { value: allRows.length, label: 'All' },
                          ]}
                          count={filteredRows.length}
                          rowsPerPage={this.props.rowsPerPage}
                          page={this.props.page}
                          onChangePage={this.handleChangePage}
                          onChangeRowsPerPage={this.handleChangeRowsPerPage}
                        />
                    }
                    showFilters={this.state.showFilters}
                    filtersComponent={
                        <Slide
                          in={this.state.showFilters}
                          mountOnEnter
                          unmountOnExit
                          timeout={300}
                        >
                            <CarrierInvoiceDetailFiltersDialog
                              open={this.state.showFilters}
                              onClose={this.onFiltersClose}
                              onChange={this.onFiltersInputChange}
                              filtersMap={this.state.filtersMap}
                              clearFilters={this.clearFiltersMap}
                              onTagClicked={this.onTagClicked}
                            />
                        </Slide>
                    }
                    filterIcon={
                        <Tooltip title={'Filter'} style={{ paddingTop: 10 }}>
                            <IconButton
                              className="filterButton"
                              size="small"
                              aria-label={'Filter'}
                              onClick={this.toggleFiltersDialog}
                            >
                                <FilterIcon />
                            </IconButton>
                        </Tooltip>
                    }
                  />

                  <TableContainer>
                      <Table size="small" style={{ tableLayout: 'auto' }}>
                          <TableHead>
                              <TableRow>
                                  {showTrackingId &&
                                    this.sortCell('trackingId', 'Tracking ID')}
                                  {showCustomerRef &&
                                    this.sortCell(
                                      'customerReference',
                                      'Customer Reference'
                                    )}
                                  {showShipDate && this.sortCell('shipDate', 'Ship Date')}
                                  {showDelivered && this.sortCell('delivered', 'Delivered')}
                                  {showServiceType &&
                                    this.sortCell('serviceType', 'Service Type')}
                                  {showZone && this.sortCell('zone', 'Zone', 'center')}
                                  {showPackages &&
                                    this.sortCell('packages', 'Pkgs', 'center')}
                                  {showActualWeight &&
                                    this.sortCell('actualWeight', 'Act. Wt.', 'right')}
                                  {showRatedWeight &&
                                    this.sortCell('ratedWeight', 'Rated Wt.', 'right')}

                                  {/* Recipient address — no shrink, sortable */}
                                  <TableCell style={{ ...noShrink, minWidth: 220 }}>
                                      <TableSortLabel
                                        active={orderBy === 'recipientAddress'}
                                        direction={
                                            orderBy === 'recipientAddress' ? order : 'asc'
                                        }
                                        onClick={() =>
                                          this.handleRequestSort('recipientAddress')
                                        }
                                      >
                                          Recipient Address
                                      </TableSortLabel>
                                  </TableCell>

                                  {/* Address match — no sort label, no shrink */}
                                  <TableCell align="center" style={noShrink} />

                                  {/* Charge columns — two-line header, sortable */}
                                  {visibleChargeColumns.map((col) => (
                                    <TableCell
                                      key={col.key}
                                      align="right"
                                      className="charge-header"
                                      style={noShrink}
                                    >
                                        <TableSortLabel
                                          active={orderBy === col.key}
                                          direction={orderBy === col.key ? order : 'asc'}
                                          onClick={() => this.handleRequestSort(col.key)}
                                        >
                                            <ChargeColHeader label={col.label} />
                                        </TableSortLabel>
                                    </TableCell>
                                  ))}

                                  {this.sortCell('invoiceTotal', 'Invoice Total', 'right')}
                                  {this.sortCell('quotedRate', 'Quoted Rate', 'right')}
                                  {this.sortCell('match', 'Match', 'center')}
                              </TableRow>
                          </TableHead>

                          <TableBody>
                              {pagedRows.map((row, i) => {
                                  const mismatch = row._mismatch;
                                  const addrMatch = row._addressMatch;
                                  const hasRate = row.shipmentRate != null;
                                  const invoiceTotal = parseDecimal(
                                    row.charges?.total ?? row.charges?.subtotal
                                  );

                                  const invoiceAddr = shortAddrFromRecipient(row.recipient);
                                  const invoiceFullAddr = fullAddrFromRecipient(
                                    row.recipient
                                  );

                                  return (
                                    <TableRow
                                      hover
                                      key={row.carrierInvoiceItemId || i}
                                      className={mismatch ? 'detail-row--mismatch' : ''}
                                    >
                                        {showTrackingId && (
                                          <TableCell
                                            style={{ fontSize: '0.875rem', ...noShrink }}
                                          >
                                              {row.trackingId}
                                          </TableCell>
                                        )}
                                        {showCustomerRef && (
                                          <TableCell
                                            style={{ fontSize: '0.875rem', ...noShrink }}
                                          >
                                              {row.details?.customerReference}
                                          </TableCell>
                                        )}
                                        {showShipDate && (
                                          <TableCell style={noShrink}>
                                              {row.details?.shipDate}
                                          </TableCell>
                                        )}
                                        {showDelivered && (
                                          <TableCell style={noShrink}>
                                              {row.details?.delivered}
                                          </TableCell>
                                        )}
                                        {showServiceType && (
                                          <TableCell style={noShrink}>
                                              {row.details?.serviceType}
                                          </TableCell>
                                        )}
                                        {showZone && (
                                          <TableCell align="center" style={noShrink}>
                                              {row.details?.zone}
                                          </TableCell>
                                        )}
                                        {showPackages && (
                                          <TableCell align="center" style={noShrink}>
                                              {row.details?.packages}
                                          </TableCell>
                                        )}
                                        {showActualWeight && (
                                          <TableCell align="right" style={noShrink}>
                                              {row.details?.actualWeight}
                                          </TableCell>
                                        )}
                                        {showRatedWeight && (
                                          <TableCell align="right" style={noShrink}>
                                              {row.details?.ratedWeight}
                                          </TableCell>
                                        )}

                                        <TableCell style={{ minWidth: 220 }}>
                                            <Tooltip
                                              title={
                                                  <div style={{ fontSize: '0.875rem' }}>
                                                      <div>
                                                          <strong>Invoice:</strong>{' '}
                                                          {invoiceFullAddr || '—'}
                                                      </div>
                                                      {row._shipmentAddress && (
                                                        <div>
                                                            <strong>Shipment:</strong>{' '}
                                                            {row._shipmentAddress}
                                                        </div>
                                                      )}
                                                  </div>
                                              }
                                              arrow
                                              enterDelay={100}
                                            >
                              <span className="detail-addr-cell">
                                {invoiceAddr || '—'}
                              </span>
                                            </Tooltip>
                                        </TableCell>

                                        <TableCell align="center" style={noShrink}>
                                            <AddressBadge match={addrMatch} />
                                        </TableCell>

                                        {visibleChargeColumns.map((col) => (
                                          <TableCell
                                            key={col.key}
                                            className="charge-cell"
                                            align="right"
                                            style={noShrink}
                                          >
                                              <ChargeBadge
                                                value={getNestedValue(row.charges, col.key)}
                                              />
                                          </TableCell>
                                        ))}

                                        <TableCell
                                          className="charge-cell charge-cell--total"
                                          style={noShrink}
                                        >
                                            {invoiceTotal != null
                                              ? `$${invoiceTotal.toFixed(2)}`
                                              : '—'}
                                        </TableCell>
                                        <TableCell
                                          className="charge-cell charge-cell--quoted"
                                          style={noShrink}
                                        >
                                            {hasRate
                                              ? `$${parseFloat(row.shipmentRate).toFixed(2)}`
                                              : '—'}
                                        </TableCell>
                                        <TableCell align="center" style={noShrink}>
                                            <MatchBadge mismatch={mismatch} hasRate={hasRate} />
                                        </TableCell>
                                    </TableRow>
                                  );
                              })}

                              {/* ── Totals row ── */}
                              {totals && (
                                <TableRow className="detail-totals-row">
                                    <TableCell
                                      colSpan={baseColCount}
                                      className="detail-totals-label"
                                    />

                                    {visibleChargeColumns.map((col) => (
                                      <TableCell
                                        key={col.key}
                                        className="charge-cell"
                                        align="right"
                                        style={noShrink}
                                      >
                                          <ChargeBadge value={totals[col.key] || 0} />
                                      </TableCell>
                                    ))}

                                    <TableCell
                                      className="charge-cell charge-cell--total"
                                      align="right"
                                      style={noShrink}
                                    >
                                        ${totals._invoiceTotal.toFixed(2)}
                                    </TableCell>
                                    <TableCell
                                      className="charge-cell charge-cell--quoted"
                                      align="right"
                                      style={noShrink}
                                    >
                                        ${totals._quotedTotal.toFixed(2)}
                                    </TableCell>

                                    {/* Variance in totals row — red when >= 0.01 */}
                                    <TableCell align="center" style={noShrink}>
                          <span
                            className={
                                Math.abs(variance) >= 0.01
                                  ? 'detail-variance--over'
                                  : 'detail-variance--ok'
                            }
                          >
                            {variance >= 0 ? '+' : ''}${variance.toFixed(2)}
                          </span>
                                    </TableCell>
                                </TableRow>
                              )}
                          </TableBody>
                      </Table>
                  </TableContainer>
              </div>
          </div>
        );
    }
}

export default CarrierInvoiceDetailView;